import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {AuthState} from './auth.reducer';
import {
  AuthActionTypes,
  CallBackAfterRegistrationAction,
  LoginAction,
  LoginActionFailure,
  LoginActionSuccess,
  LoginCallBackAction,
  LoginWaitAction
} from './auth.actions';
import {map, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {AuthService} from '../../services/auth.service';
import {saveToLocalStorage} from '../actions/store.actions';
import {authorize, getUserData, loginAsLocationUser} from '../actions/user.actions';
import {IAppState} from '../../models/IAppState';
import {IAuthData} from '../../models/IAuthData';
import {AppConfigService} from '../../services/appconfig.service';
import {IOidcData} from '../../models/IOidcData';
import {Observable} from 'rxjs';
import {CometchatService} from '../../services/cometchat.service';
import {Platform} from '@ionic/angular';
import {User} from 'oidc-client';

@Injectable()
export class AuthEffects {

  private authData: IAuthData;
  public authData$: Observable<IAuthData> = this.appState.select(
    (state) => state.AppStateReducer.authData
  );
  public isAuthorised$: Observable<boolean> = this.appState.select(
    (state) => state.AppStateReducer.isAuthorised
  );

  public locationUser$: Observable<User> = this.appState.select(
    (state) => state.AppStateReducer.locationUser
  );
  constructor(
    private actions$: Actions,
    private store: Store<AuthState>,
    private appState: Store<{ AppStateReducer: IAppState }>,
    private router: Router,
    private authService: AuthService,
    private cometChatService: CometchatService,
    private appConfig: AppConfigService,
    private platform: Platform
  ) {
    this.authData$.subscribe((authData: IAuthData) => {
      if (authData) {
        console.log('authData');
        this.authData = authData;
        this.store.dispatch(getUserData({} as any));
        this.store.dispatch(saveToLocalStorage());
      }
    });
    this.isAuthorised$.subscribe((isAuthorised: boolean) => {
      if (isAuthorised) {
        console.log('isAuthorised');
        this.store.dispatch(getUserData({} as any));
      }
    });
    this.locationUser$.subscribe((locationUser: User) => {
      if (locationUser) {
        console.log('locationUser');
        this.store.dispatch(getUserData({} as any));
      }
    });
  }



  login$ = createEffect(() => this.actions$.pipe(
    ofType<LoginAction>(AuthActionTypes.LoginAction),
    tap(async () => {
      try {
        if (this.platform.is('hybrid') && (this.platform.is('android') || this.platform.is('ios'))) {
          await this.authService.signInPopup().then(() => {
            this.store.dispatch(new LoginActionSuccess({
              user: this.authService.getUser(),
              isAuthorized: true
            }));
          });
        } else {
          await this.authService.startAuthentication();
        }
        this.store.dispatch(new LoginWaitAction());
        this.cometChatService.userLogout();
        // setTimeout(() => window.location.reload(), 1);
      } catch (error) {
        this.store.dispatch(new LoginActionFailure({ isAuthorized: false }));
      }
    })
  ), { dispatch: false });

  loginSuccess$ = createEffect(() => this.actions$.pipe(
    ofType<LoginActionSuccess>(AuthActionTypes.LoginActionSuccess),
    map((action) => {
      if (this.platform.is('hybrid') && (this.platform.is('android') || this.platform.is('ios'))) {
        this.getLoginCookie(action.payload.user?.profile?.name);
      }
      if (!!this.authData) {
        this.store.dispatch(loginAsLocationUser({ loggedInAsLocationUser: true, locationUser: action.payload.user }));
      } else {
        this.store.dispatch(loginAsLocationUser({ loggedInAsLocationUser: false, locationUser: action.payload.user }));
      }
      this.appState.dispatch(authorize(({ isAuthorised: true, authData: this.authData })));
      this.appState.dispatch(saveToLocalStorage());

      let navigation: Promise<boolean>;
      if (localStorage.unauthUrl) {
        navigation = this.router.navigate([localStorage.unauthUrl], { replaceUrl: true });
        localStorage.removeItem('unauthUrl');
      } else {
        navigation = this.router.navigate(['/checkin-list/ticket'], { replaceUrl: true });
      }
      void navigation.then(
        () => this.authService.hideAuthReturnLoading(),
        () => this.authService.hideAuthReturnLoading()
      );
    })
  ), { dispatch: false });

  loginFailure$ = createEffect(() => this.actions$.pipe(
    ofType<LoginActionFailure>(AuthActionTypes.LoginActionFailure),
    map(() => {
      void this.authService.hideAuthReturnLoading();
      // this.store.dispatch(new LoginAction());
    })
  ), { dispatch: false });

  loginCallBack$ = createEffect(() => this.actions$.pipe(
    ofType<LoginCallBackAction>(AuthActionTypes.LoginCallBackAction),
    tap(async () => {
      // needed when login with popup and 'popupWindowTarget' is '_blank'
      // if (this.platform.is('hybrid') && (this.platform.is('android') || this.platform.is('ios'))) {
      //  await this.authService.signInPopUpCallback().then(() => {
      //    this.store.dispatch(new LoginActionSuccess({
      //      user: this.authService.getUser(),
      //      isAuthorized: true
      //    }));
      //  }).catch(() => {
      //    this.store.dispatch(new LoginActionFailure({ isAuthorized: false }));
      //  });
      // } else {
      await this.authService.completeAuthentication().then(() => {
        this.store.dispatch(new LoginActionSuccess({
          user: this.authService.getUser(),
          isAuthorized: true
        }));
      }).catch(() => {
        this.store.dispatch(new LoginActionFailure({ isAuthorized: false }));
      });
      // }
    })
  ), { dispatch: false });

  registrationCallBack$ = createEffect(() => this.actions$.pipe(
    ofType<CallBackAfterRegistrationAction>(AuthActionTypes.CallBackAfterRegistrationAction),
    tap(async () => {
      const currentDateTime = new Date();
      const oidcData: IOidcData = {
        id: this.appConfig.config.identityConfig.state,
        created: currentDateTime.getTime() / 1000,
        request_type: 'si:p',
        code_verifier: this.appConfig.config.identityConfig.codeVerifier,
        redirect_uri: this.appConfig.config.identityConfig.hostUrl + 'registration_auth_callback',
        authority: this.appConfig.config.identityConfig.identityEndpoint,
        client_id: this.appConfig.config.identityConfig.clientId,
        response_mode: 'query',
        scope: this.appConfig.config.identityConfig.requiredScopes
      };

      localStorage.setItem('oidc.' + this.appConfig.config.identityConfig.state, JSON.stringify(oidcData));

      await this.authService.completeAuthentication().then(() => {
        this.store.dispatch(new LoginActionSuccess({
          user: this.authService.getUser(),
          isAuthorized: true
        }));
      }).catch(() => {
        this.store.dispatch(new LoginActionFailure({ isAuthorized: false }));
      });
    })
  ), { dispatch: false });

  private getLoginCookie(userName: string): void {
    this.authService.getLoginCookie(userName).subscribe(() => {
      console.log('cookie received');
    }, err => {
      console.log(err);
    });
  }
}
